import React, { useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook, ChevronRight, Home, CheckCircle, XCircle } from 'lucide-react';
import {
    fetchCategoriasProducto,
    CategoriaProducto,
} from '../../../services/categoriaProductoService';
import AddProductsCategory from '../../../components/ProductsCategory/AddProductsCategory';
import EditProductsCategory from '../../../components/ProductsCategory/EditProductsCategory';
import DeleteProductsCategory from '../../../components/ProductsCategory/DeleteProductsCategory';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const C = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryDark: '#004d7a',
    secondaryLight: '#8b3fa8',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.pp-wrap * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes ppFadeUp  { from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);} }
@keyframes ppBlob    { 0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;} }
@keyframes spin { to{transform:rotate(360deg);} }
.pp-fadeup   { animation:ppFadeUp .45s cubic-bezier(.16,1,.3,1) both; }
.pp-fadeup-1 { animation:ppFadeUp .45s .08s cubic-bezier(.16,1,.3,1) both; }
.pp-card { background:#fff;border-radius:20px;border:1.5px solid #f1f5f9;box-shadow:0 2px 12px rgba(0,0,0,.04);overflow:hidden; }
.pp-table-row { transition:background .15s; }
.pp-table-row:hover { background:#f8fafc; }
.pp-action-btn { width:30px;height:30px;border-radius:9px;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s; }
.pp-action-btn.edit { background:${C.secondary}10;color:${C.secondary}; }
.pp-action-btn.edit:hover { background:${C.secondary}20;transform:translateY(-1px); }
.pp-action-btn.del  { background:${C.primary}10;color:${C.primary}; }
.pp-action-btn.del:hover  { background:${C.primary}20;transform:translateY(-1px); }
.pp-scroll::-webkit-scrollbar{width:5px;height:5px;}
.pp-scroll::-webkit-scrollbar-track{background:transparent;}
.pp-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.25);border-radius:10px;}
`;

const ExpandableComment = ({ comment }: { comment?: string | null }) => {
    const [expanded, setExpanded] = useState(false);
    const words = (comment || '').split(' ');
    const truncated = words.slice(0, 5).join(' ') + (words.length > 5 ? '…' : '');
    if (!comment)
        return (
            <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: '.8rem' }}>
                Sin comentarios
            </span>
        );
    return (
        <span
            onClick={() => words.length > 5 && setExpanded(!expanded)}
            style={{
                cursor: words.length > 5 ? 'pointer' : 'default',
                fontSize: '.82rem',
                color: '#64748b',
            }}
            onMouseEnter={e => words.length > 5 && ((e.currentTarget as HTMLSpanElement).style.color = C.secondary)}
            onMouseLeave={e => ((e.currentTarget as HTMLSpanElement).style.color = '#64748b')}
        >
            {expanded ? comment : truncated}
        </span>
    );
};

const ProductsCategoryPage: React.FC = () => {
    const [categorias, setCategorias] = useState<CategoriaProducto[]>([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoriaProducto | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<CategoriaProducto | null>(null);
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('categoriaproducto', userLevelId); // ajusta el nombre del modelo según tu backend

    useEffect(() => {
        const load = async () => {
            try {
                if (perm?.can_read) {
                    const data = await fetchCategoriasProducto();
                    setCategorias(data.sort((a, b) => a.nombre.localeCompare(b.nombre)));
                } else {
                    setCategorias([]);
                }
            } catch {
                setCategorias([]);
            }
        };
        load();
    }, [perm]);

    const handleCategoryCreated = (cat: CategoriaProducto) =>
        setCategorias(prev => [...prev, cat]);
    const handleCategoryUpdated = (cat: CategoriaProducto) =>
        setCategorias(prev => prev.map(c => (c.id === cat.id ? cat : c)));
    const handleCategoryDeleted = (id: number) =>
        setCategorias(prev => prev.filter(c => c.id !== id));
    const handleDeleteClick = (cat: CategoriaProducto) => {
        setCategoryToDelete(cat);
        setIsDeleteModalOpen(true);
    };

    const totalPages = Math.ceil(categorias.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedCategorias = categorias.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando categorías de productos..." />;

    return (
        <>
            <style>{css}</style>
            <div
                className="pp-wrap"
                style={{
                    background: '#fff',
                    minHeight: '100vh',
                    padding: 'clamp(24px,3vw,36px) clamp(20px,4vw,40px)',
                    position: 'relative',
                    overflowX: 'clip',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: '-60px',
                        right: '-60px',
                        width: '320px',
                        height: '320px',
                        background: `linear-gradient(135deg,${C.primary}08,${C.secondary}06)`,
                        animation: 'ppBlob 8s ease-in-out infinite',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        bottom: '60px',
                        left: '20px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5,1fr)',
                        gap: '6px',
                        opacity: 0.07,
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                >
                    {Array.from({ length: 25 }).map((_, i) => (
                        <div
                            key={i}
                            style={{
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                background: '#0f172a',
                            }}
                        />
                    ))}
                </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                    {/* HEADER */}
                    <div className="pp-fadeup" style={{ marginBottom: '28px' }}>
                        <nav
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '.72rem',
                                fontWeight: '600',
                                color: '#94a3b8',
                                marginBottom: '12px',
                            }}
                        >
                            <a
                                href="/dashboard"
                                style={{
                                    color: '#94a3b8',
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.color = C.primary)}
                                onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                            >
                                <Home size={11} /> Dashboard
                            </a>
                            <ChevronRight size={10} color="#e2e8f0" />
                            <span style={{ color: C.primary, fontWeight: '700' }}>
                                Categorías de Productos
                            </span>
                        </nav>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '16px',
                            }}
                        >
                            <div>
                                <h1
                                    style={{
                                        fontSize: 'clamp(1.5rem,3vw,2rem)',
                                        fontWeight: '900',
                                        color: '#0f172a',
                                        letterSpacing: '-.025em',
                                        lineHeight: 1.1,
                                        marginBottom: '6px',
                                    }}
                                >
                                    Gestión de{' '}
                                    <span style={{ color: C.primary }}>Categorías de Productos</span>
                                </h1>
                                <p style={{ fontSize: '.85rem', color: '#64748b', fontWeight: '500' }}>
                                    Administra las categorías de los productos de la cafetería
                                </p>
                                <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                                    {[C.primary, C.secondary, C.accentDark, C.secondaryLight].map(
                                        (c, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    height: '3px',
                                                    width: i === 0 ? '24px' : '8px',
                                                    borderRadius: '3px',
                                                    background: c,
                                                }}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                    onClick={() => navigate('/categories/history')} // ruta hipotética
                                    style={{
                                        width: '38px',
                                        height: '38px',
                                        borderRadius: '11px',
                                        border: `1.5px solid ${C.secondary}25`,
                                        background: `${C.secondary}08`,
                                        color: C.secondary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all .2s',
                                    }}
                                    onMouseEnter={e =>
                                        ((e.currentTarget as HTMLButtonElement).style.background = `${C.secondary}15`)
                                    }
                                    onMouseLeave={e =>
                                        ((e.currentTarget as HTMLButtonElement).style.background = `${C.secondary}08`)
                                    }
                                >
                                    <History size={16} />
                                </button>
                                {perm.can_create && (
                                    <button
                                        onClick={() => setIsAddModalOpen(true)}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '7px',
                                            padding: '9px 20px',
                                            borderRadius: '11px',
                                            border: 'none',
                                            background: `linear-gradient(135deg,${C.primary},${C.primaryDark})`,
                                            color: '#fff',
                                            fontSize: '.88rem',
                                            fontWeight: '800',
                                            cursor: 'pointer',
                                            boxShadow: `0 4px 14px ${C.primary}28`,
                                            transition: 'all .22s',
                                        }}
                                        onMouseEnter={e =>
                                        ((e.currentTarget as HTMLButtonElement).style.transform =
                                            'translateY(-2px)')
                                        }
                                        onMouseLeave={e =>
                                        ((e.currentTarget as HTMLButtonElement).style.transform =
                                            'translateY(0)')
                                        }
                                    >
                                        <Plus size={16} /> Agregar Categoría
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* CARD */}
                    <div className="pp-card pp-fadeup-1">
                        {/* Header card: contador */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '14px 20px',
                                borderBottom: '1.5px solid #f1f5f9',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '9px',
                                        background: `${C.secondary}10`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Notebook size={14} color={C.secondary} />
                                </div>
                                <span style={{ fontSize: '.82rem', fontWeight: '700', color: '#374151' }}>
                                    <span style={{ color: C.secondary, fontWeight: '900' }}>
                                        {categorias.length}
                                    </span>{' '}
                                    {categorias.length === 1
                                        ? 'categoría registrada'
                                        : 'categorías registradas'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <div
                                    style={{
                                        width: '7px',
                                        height: '7px',
                                        borderRadius: '50%',
                                        background: C.secondaryLight,
                                    }}
                                />
                                <span style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: '600' }}>
                                    Actualizado
                                </span>
                            </div>
                        </div>

                        <div className="pp-scroll" style={{ overflowX: 'auto' }}>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontFamily: "'DM Sans',sans-serif",
                                }}
                            >
                                <thead>
                                    <tr style={{ background: '#fafcff', borderBottom: '1.5px solid #f1f5f9' }}>
                                        {[
                                            'Nombre',
                                            'Descripción',
                                            'Sucursal',
                                            'Estado',
                                            'Acciones',
                                        ].map(h => (
                                            <th
                                                key={h}
                                                style={{
                                                    padding: '11px 18px',
                                                    textAlign: 'left',
                                                    fontSize: '.65rem',
                                                    fontWeight: '800',
                                                    color: '#94a3b8',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '.08em',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedCategorias.map(cat => (
                                        <tr
                                            key={cat.id}
                                            className="pp-table-row"
                                            style={{ borderBottom: '1px solid #f8fafc' }}
                                        >
                                            <td style={{ padding: '13px 18px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '10px',
                                                            background: `${C.secondary}10`,
                                                            border: `1.5px solid ${C.secondary}15`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <Notebook size={14} color={C.secondary} />
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontSize: '.88rem',
                                                            fontWeight: '700',
                                                            color: '#0f172a',
                                                        }}
                                                    >
                                                        {cat.nombre}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '13px 18px', maxWidth: '200px' }}>
                                                <ExpandableComment comment={cat.descripcion} />
                                            </td>
                                            <td
                                                style={{
                                                    padding: '13px 18px',
                                                    fontSize: '.8rem',
                                                    color: '#64748b',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                {cat.sucursal_info?.nombre || `ID ${cat.sucursal}`}
                                            </td>
                                            <td style={{ padding: '13px 18px' }}>
                                                {cat.activo ? (
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            padding: '3px 10px',
                                                            borderRadius: '100px',
                                                            fontSize: '.68rem',
                                                            fontWeight: '700',
                                                            background: `${C.secondaryLight}14`,
                                                            color: '#065f46',
                                                            border: `1px solid ${C.secondaryLight}30`,
                                                        }}
                                                    >
                                                        <CheckCircle size={10} /> Activo
                                                    </span>
                                                ) : (
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            padding: '3px 10px',
                                                            borderRadius: '100px',
                                                            fontSize: '.68rem',
                                                            fontWeight: '700',
                                                            background: `${C.primary}10`,
                                                            color: C.primaryDark,
                                                            border: `1px solid ${C.primary}22`,
                                                        }}
                                                    >
                                                        <XCircle size={10} /> Inactivo
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '13px 18px' }}>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    {perm.can_update && (
                                                        <button
                                                            className="pp-action-btn edit"
                                                            onClick={() => {
                                                                setSelectedCategory(cat);
                                                                setIsEditModalOpen(true);
                                                            }}
                                                        >
                                                            <Edit size={13} />
                                                        </button>
                                                    )}
                                                    {perm.can_delete && (
                                                        <button
                                                            className="pp-action-btn del"
                                                            onClick={() => handleDeleteClick(cat)}
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {perm.can_read && categorias.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                style={{ padding: '56px 24px', textAlign: 'center' }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '56px',
                                                            height: '56px',
                                                            borderRadius: '16px',
                                                            background: `${C.secondary}10`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Notebook
                                                            size={24}
                                                            color={C.secondary}
                                                            style={{ opacity: 0.4 }}
                                                        />
                                                    </div>
                                                    <p style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a' }}>
                                                        No hay categorías disponibles
                                                    </p>
                                                    {perm.can_create && (
                                                        <p style={{ fontSize: '.85rem', color: '#94a3b8' }}>
                                                            Agrega una nueva categoría para comenzar
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {perm.can_read && categorias.length > 0 && (
                            <div style={{ borderTop: '1.5px solid #f1f5f9' }}>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={categorias.length}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AddProductsCategory
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onCreated={handleCategoryCreated}
            />
            <EditProductsCategory
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedCategory(null);
                }}
                initialData={selectedCategory}
                onUpdate={handleCategoryUpdated}
            />
            <DeleteProductsCategory
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                categoryId={categoryToDelete?.id ?? null}
                categoryName={categoryToDelete?.nombre ?? ''}
                onDeleted={handleCategoryDeleted}
            />
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            />
        </>
    );
};

export default ProductsCategoryPage;